import type { Contract } from '@gmn-portal/backend';
import { ChevronDown, ChevronRight, CornerDownRight, MoreHorizontal } from 'lucide-react';
import { DateTime } from 'luxon';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { Id } from 'react-calendar-timeline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomer } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui';
import { TimelineView, type TimelineViewHandle } from '@/shared/components/ui/timeline';
import { cn } from '@/shared/utils';

interface ContractsTimelineProps {
  contracts: Contract[];
  groupByCustomer?: boolean;
  onDelete?: (contract: Contract) => void;
  onCopy?: (contract: Contract) => void;
}

export interface ContractsTimelineHandle {
  handleExpandAll: () => void;
  handleCollapseAll: () => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
}

interface CustomerTimelineItemProps {
  customerId: string;
  startTime: number;
  endTime: number;
}

const CustomerTimelineItemTitle = ({ customerId }: { customerId: string }) => {
  const { data: customer } = useCustomer(customerId);
  return <span className="text-xs text-primary-foreground">{customer?.name || '-'}</span>;
};

function CustomerTimelineItem({ customerId, startTime, endTime }: CustomerTimelineItemProps) {
  return {
    id: `customer-span-${customerId}`,
    group: `customer-${customerId}`,
    title: <CustomerTimelineItemTitle customerId={customerId} />,
    start_time: startTime,
    end_time: endTime,
    className: cn('bg-primary! border-none!'),
    itemProps: {
      style: { display: 'none' },
    },
  };
}

function CustomerGroup({
  id,
  isExpanded,
  onToggle,
}: {
  id: string;
  isExpanded?: boolean;
  onToggle?: () => void;
}) {
  const { data: customer } = useCustomer(id);
  const customerName = customer?.name || '-';
  const customerAbbreviation =
    customerName === '-' ? '--' : customerName.substring(0, 2).toUpperCase();

  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-2 w-full h-full px-2 text-left text-sm rounded-none hover:bg-accent/50"
    >
      <span className="text-muted-foreground">
        {isExpanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
      </span>
      <div className="size-6 rounded-md bg-secondary text-primary flex items-center justify-center font-bold text-[10px]">
        {customerAbbreviation}
      </div>
      <span className="truncate font-medium">{customerName}</span>
    </button>
  );
}

type RelationshipLike = string | { id: string } | null | undefined;

const getRelationshipId = (value: RelationshipLike): string | null => {
  if (!value) return null;
  return typeof value === 'string' ? value : value.id || null;
};

const sortContractsForCustomerGroup = (contracts: Contract[]) => {
  const rootContracts = contracts.filter(
    (c) => !getRelationshipId((c as any).parentContract as RelationshipLike)
  );
  const subcontracts = contracts.filter((c) =>
    Boolean(getRelationshipId((c as any).parentContract as RelationshipLike))
  );

  const subcontractsByParent = subcontracts.reduce(
    (acc, subcontract) => {
      const parentId = getRelationshipId((subcontract as any).parentContract as RelationshipLike);
      if (!parentId) return acc;
      const siblings = acc[parentId] ?? [];
      siblings.push(subcontract);
      acc[parentId] = siblings;
      return acc;
    },
    {} as Record<string, Contract[]>
  );

  const ordered: Contract[] = [];
  rootContracts.forEach((root) => {
    ordered.push(root);
    const children = subcontractsByParent[root.id] || [];
    ordered.push(...children);
  });

  // Fall back: include any orphaned subcontracts (e.g. missing parent in list).
  const included = new Set(ordered.map((c) => c.id));
  subcontracts.forEach((c) => {
    if (!included.has(c.id)) ordered.push(c);
  });

  return ordered;
};

export const ContractsTimeline = forwardRef<ContractsTimelineHandle, ContractsTimelineProps>(
  ({ contracts, groupByCustomer = false, onDelete, onCopy }, ref) => {
    const navigate = useNavigate();
    const { t } = useIntl();
    const [searchParams] = useSearchParams();
    const basePath = '/gmn/contract-management/contracts';
    const timelineRef = useRef<TimelineViewHandle>(null);

    // Initialize expandedCustomers with all customers expanded
    const initialExpandedCustomers = useMemo(() => {
      if (!groupByCustomer) return {};
      return contracts.reduce(
        (acc, contract) => {
          const customerId =
            typeof contract.customer === 'string' ? contract.customer : contract.customer?.id;
          if (customerId) {
            acc[customerId] = true;
          }
          return acc;
        },
        {} as Record<string, boolean>
      );
    }, [contracts, groupByCustomer]);

    const [expandedCustomers, setExpandedCustomers] =
      useState<Record<string, boolean>>(initialExpandedCustomers);

    // Pre-calculate customer timeline spans
    const customerSpans = useMemo(() => {
      if (!groupByCustomer) return [];

      // Group contracts by customer and calculate spans
      const spans = contracts.reduce(
        (acc, contract) => {
          const customerField = contract.customer;
          const customerId = typeof customerField === 'string' ? customerField : customerField?.id;
          if (!customerId) return acc;

          if (!acc[customerId]) {
            acc[customerId] = {
              id: customerId,
              startDate: DateTime.fromISO(contract.startDate!),
              endDate: DateTime.fromISO(contract.endDate!),
            };
          } else {
            const contractStartDate = DateTime.fromISO(contract.startDate!);
            if (contractStartDate < acc[customerId].startDate) {
              acc[customerId].startDate = contractStartDate;
            }

            const contractEndDate = DateTime.fromISO(contract.endDate!);
            if (contractEndDate > acc[customerId].endDate) {
              acc[customerId].endDate = contractEndDate;
            }
          }

          return acc;
        },
        {} as Record<
          string,
          {
            id: string;
            startDate: DateTime;
            endDate: DateTime;
          }
        >
      );

      return Object.entries(spans).map(([customerId, span]) => ({
        customerId,
        startTime: span.startDate.toMillis(),
        endTime: span.endDate.toMillis(),
      }));
    }, [contracts, groupByCustomer]);

    const toggleCustomer = (id: string) => {
      setExpandedCustomers((prev) => ({
        ...prev,
        [id]: !prev[id],
      }));
    };

    const handleExpandAll = () => {
      // Expand all customers
      const allCustomers = contracts.reduce(
        (acc, contract) => {
          const customerField = contract.customer;
          const customerId = typeof customerField === 'string' ? customerField : customerField?.id;
          if (customerId) {
            acc[customerId] = true;
          }
          return acc;
        },
        {} as Record<string, boolean>
      );

      setExpandedCustomers(allCustomers);
    };

    const handleCollapseAll = () => {
      setExpandedCustomers({});
    };

    const handleZoomIn = () => {
      timelineRef.current?.handleZoomIn();
    };

    const handleZoomOut = () => {
      timelineRef.current?.handleZoomOut();
    };

    useImperativeHandle(ref, () => ({
      handleExpandAll,
      handleCollapseAll,
      handleZoomIn,
      handleZoomOut,
    }));

    const renderContractSidebarItem = (contract: Contract, depth: 0 | 1 | 2) => {
      const status = contract.status as 'open' | 'closed' | undefined;
      const statusLabel = status ? t(`contracts.status.${status}`) : null;
      const orderNumber = contract.orderNumber?.trim() || '-';
      const indentClass = depth === 0 ? 'pl-2' : depth === 1 ? 'pl-14' : 'pl-20';

      return (
        <div className={cn('flex min-w-0 flex-col leading-tight pr-2', indentClass)}>
          <div className="flex items-center gap-2 min-w-0">
            {depth === 2 ? (
              <CornerDownRight className="size-3 text-muted-foreground shrink-0" />
            ) : null}
            <span className="truncate text-sm text-foreground">{contract.title}</span>
            {statusLabel ? (
              <Badge
                variant="outline-solid"
                className={cn(
                  'shrink-0 px-2 py-0 text-[10px] leading-4',
                  status === 'closed' ? 'text-muted-foreground' : 'text-foreground'
                )}
              >
                {statusLabel}
              </Badge>
            ) : null}
          </div>
          <span className="truncate text-xs text-muted-foreground">{orderNumber}</span>
        </div>
      );
    };

    const handleNavigate = (contract: Contract) => {
      const queryString = searchParams.toString();
      const searchQuery = queryString ? `?${queryString}` : '';

      navigate(`${basePath}/${contract.id}${searchQuery}`);
    };

    // Create customer timeline items
    const customerTimelineItems = customerSpans.map((span) =>
      CustomerTimelineItem({
        customerId: span.customerId,
        startTime: span.startTime,
        endTime: span.endTime,
      })
    );

    const { timelineGroups, timelineItems } = groupByCustomer
      ? // Group by customer
        {
          timelineGroups: Object.values(
            contracts.reduce(
              (acc, contract) => {
                const customerField = contract.customer;
                const customerId =
                  typeof customerField === 'string' ? customerField : customerField?.id;
                if (!customerId) return acc;

                if (!acc[customerId]) {
                  acc[customerId] = {
                    id: customerId,
                    title: null,
                    contracts: [],
                  };
                }
                acc[customerId].contracts.push(contract);
                return acc;
              },
              {} as Record<string, { id: string; title: null; contracts: Contract[] }>
            )
          ).flatMap((customerGroup) => {
            return [
              // Customer group
              {
                id: `customer-${customerGroup.id}` as Id,
                title: (
                  <CustomerGroup
                    id={customerGroup.id}
                    isExpanded={expandedCustomers[customerGroup.id]}
                    onToggle={() => toggleCustomer(customerGroup.id)}
                  />
                ),
                root: true,
                height: 52,
              },
              // Contracts under the customer (flat) when expanded
              ...(expandedCustomers[customerGroup.id]
                ? sortContractsForCustomerGroup(customerGroup.contracts).map((o) => ({
                    id: o.id as Id,
                    title: renderContractSidebarItem(
                      o,
                      getRelationshipId((o as any).parentContract as RelationshipLike) ? 2 : 1
                    ),
                    root: false,
                  }))
                : []),
            ];
          }),
          timelineItems: [
            // Customer-level timeline items that span from earliest to latest contract dates
            ...customerTimelineItems,
            // Regular contract items
            ...contracts
              .filter((contract) => {
                const customerField = contract.customer;
                const customerId =
                  typeof customerField === 'string' ? customerField : customerField?.id;
                return customerId ? expandedCustomers[customerId] : false;
              })
              .map((contract) => ({
                id: contract.id,
                group: contract.id,
                title: (
                  <div className="flex gap-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <div className="relative size-5">
                          <Button
                            variant="ghost"
                            className="absolute left-0 top-[7px] size-5 p-0 text-primary-foreground/80 hover:text-primary-foreground"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="sr-only">{t('navigation.openMenu')}</span>
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleNavigate(contract);
                          }}
                        >
                          {t('common.edit')}
                        </DropdownMenuItem>
                        {onCopy && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onCopy(contract);
                            }}
                          >
                            {t('contracts.copyContract')}
                          </DropdownMenuItem>
                        )}
                        {onDelete && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(contract);
                            }}
                          >
                            {t('common.delete')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <span className="text-primary-foreground">{contract.title}</span>
                  </div>
                ),
                start_time: DateTime.fromISO(contract.startDate!).toMillis(),
                end_time: DateTime.fromISO(contract.endDate!).toMillis(),
                className: cn('bg-primary! border-none!'),
              })),
          ],
        }
      : // Show each contract as its own group
        {
          timelineGroups: contracts.map((contract) => ({
            id: contract.id,
            title: renderContractSidebarItem(contract, 0),
          })),
          timelineItems: contracts.map((contract) => ({
            id: contract.id,
            group: contract.id,
            title: (
              <div className="flex gap-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <div className="relative size-5">
                      <Button
                        variant="ghost"
                        className="absolute left-0 top-1 size-5 p-0 text-primary-foreground/80 hover:text-primary-foreground"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="sr-only">{t('navigation.openMenu')}</span>
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleNavigate(contract);
                      }}
                    >
                      {t('common.edit')}
                    </DropdownMenuItem>
                    {onCopy && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onCopy(contract);
                        }}
                      >
                        {t('contracts.copyContract')}
                      </DropdownMenuItem>
                    )}
                    {onDelete && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(contract);
                        }}
                      >
                        {t('common.delete')}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <span className="text-primary-foreground">{contract.title}</span>
              </div>
            ),
            start_time: DateTime.fromISO(contract.startDate!).toMillis(),
            end_time: DateTime.fromISO(contract.endDate!).toMillis(),
            className: cn('bg-primary! border-none!'),
          })),
        };

    return (
      <TimelineView
        ref={timelineRef}
        groups={timelineGroups}
        items={timelineItems}
        startTime={DateTime.now().minus({ months: 1 }).toMillis()}
        endTime={DateTime.now().plus({ months: 12 }).toMillis()}
        horizontalLineClassNamesForGroup={(group) => (group.root ? ['row-root'] : [])}
        onItemClick={(itemId) => {
          const contract = contracts.find((o) => o.id === itemId);
          if (contract) {
            handleNavigate(contract);
          }
        }}
      />
    );
  }
);

ContractsTimeline.displayName = 'ContractsTimeline';
